'use client'

import { BottomCTA, Divider, Image, Text } from '@macarongfactory/web-ui'
import { Fragment } from 'react'

import {
  applyCouponDiscount,
  COUPON_APPLICABLE_SECTIONS,
} from '@/entities/reservation'
import {
  CAR_WASH_SECTION,
  ENGINE_OIL_SECTION,
  type IPlaceCategoryProduct,
  MYCLE_HOME_URL,
  PLACE_CATEGORY_PRODUCTS,
  PLACE_DETAIL_APP_DOWNLOAD_ONELINK_URL,
  TIRE_SECTION,
} from '@/features/seoPlaceDetail/lib'
import { getPlaceMinPrice, parsePrice } from '@/features/seoPlaceDetail/lib'
import {
  type IProductGroup,
  type ISeoPlace,
} from '@/features/seoPlaceDetail/model'
import { MycleAppLogo } from '@/shared/assets/icons'
import { logEvent } from '@/shared/external/firebase/analytics'
import { useSeoOnelinkUrl } from '@/shared/external/onelink'
import { CODE, EVENT_NAMES, TYPE } from '@/shared/lib'

interface IPlacePcStickyCtaProps {
  place: ISeoPlace
  productGroups: IProductGroup[]
}

// 할인율(원가 대비)을 쿠폰적용가 기준으로 계산하는 섹션(엔진오일·타이어·세차). 그 외는 판매가 기준 (Figma 스펙)
const DISCOUNT_RATE_ON_COUPON_PRICE_SECTIONS = new Set<string>([
  ENGINE_OIL_SECTION,
  TIRE_SECTION,
  CAR_WASH_SECTION,
])

const BANNER_TEXT = '앱 다운받고 2만원 쿠폰 받기'
const CTA_BUTTON_TEXT = '쿠폰받고 앱으로 예약하기'
const BANNER_BACKGROUND =
  'linear-gradient(-45.407995654187374deg, rgb(13, 62, 81) 41.765%, rgb(45, 55, 65) 58.235%)'

const PlacePcStickyCta = ({ place, productGroups }: IPlacePcStickyCtaProps) => {
  const ctaButtonUrl = useSeoOnelinkUrl(PLACE_DETAIL_APP_DOWNLOAD_ONELINK_URL)

  const minPrice = getPlaceMinPrice(productGroups)

  const productGroupBySection = new Map<string, IProductGroup>()
  productGroups.forEach(group => {
    if (group.section) productGroupBySection.set(group.section, group)
  })
  const matchedCategories = PLACE_CATEGORY_PRODUCTS.filter(category =>
    productGroupBySection.has(category.section)
  )

  return (
    <div className='sticky top-6 isolate z-10 flex flex-col overflow-hidden rounded-dimension-x6 bg-background-surface-default shadow-[0_4px_20px_rgba(0,0,0,0.06)]'>
      {/* 헤더: 정비소명 + 최저가 */}
      <div className='flex flex-col gap-dimension-x1 px-dimension-x5 py-dimension-x4'>
        <Text
          variant='title-1-bold'
          color='text-foreground-neutral-strong'
          className='truncate'
        >
          {place.placeName}
        </Text>
        {minPrice !== null && (
          <Text variant='title-1-semibold' color='text-foreground-brand-strong'>
            {minPrice.toLocaleString()}원~
          </Text>
        )}
      </div>

      {/* 띠배너 (클릭 시 프로모션 배너 원링크로 이동) */}
      <a
        href={MYCLE_HOME_URL}
        target='_blank'
        rel='noopener noreferrer'
        className='flex items-center gap-dimension-x2-5 px-dimension-x5 py-dimension-x4'
        style={{ backgroundImage: BANNER_BACKGROUND }}
      >
        <MycleAppLogo width='32' height='32' />
        <Text
          variant='title-1-bold'
          color='text-foreground-neutral-inverse'
          className='min-w-0 flex-1'
        >
          {BANNER_TEXT}
        </Text>
      </a>

      {/* 상품 리스트 (스크롤 영역은 BottomCTA 위까지만) */}
      {/* 하단 fade: web-ui BottomCTA의 Gradient와 동일하게 24px(=dimension-x6) 페이드 */}
      {matchedCategories.length > 0 && (
        <div
          style={{
            maskImage: 'linear-gradient(to top, transparent, black 24px)',
            WebkitMaskImage: 'linear-gradient(to top, transparent, black 24px)',
          }}
          className='flex max-h-[280px] flex-col gap-dimension-x4 overflow-y-auto px-dimension-x4 pt-dimension-x4 pb-dimension-x4 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-foreground-neutral-muted [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-[8px]'
        >
          {matchedCategories.map((category, index) => (
            <Fragment key={category.id}>
              {index > 0 && <Divider.Horizontal />}
              <StickyProductItem
                category={category}
                productGroup={productGroupBySection.get(category.section)}
              />
            </Fragment>
          ))}
        </div>
      )}

      {/* 하단 CTA — 스크롤 영역 바깥 flow 배치 (그라데이션은 스크롤 영역 mask-image로 대체) */}
      <BottomCTA.Vertical
        position='relative'
        gradation={false}
        mainButton={
          <BottomCTA.MainButton.SolidButton
            text={CTA_BUTTON_TEXT}
            onClick={() => {
              logEvent(EVENT_NAMES.COMMON_WEB_PLACE.ITEM.CLICK, {
                code: CODE.COMMON_WEB_PLACE.CTA,
                type: TYPE.BUTTON,
                label: CTA_BUTTON_TEXT,
              })
              window.open(ctaButtonUrl, '_blank', 'noopener,noreferrer')
            }}
          />
        }
      />
    </div>
  )
}

interface IStickyProductItemProps {
  category: IPlaceCategoryProduct
  productGroup: IProductGroup | undefined
}

const StickyProductItem = ({
  category,
  productGroup,
}: IStickyProductItemProps) => {
  const lowestProduct = productGroup?.lowestPriceTargetProduct
  const rawSalePrice = parsePrice(lowestProduct?.salePrice)
  const salePrice = applyCouponDiscount(productGroup?.section, rawSalePrice)
  // 원가격. 세차 등 원가가 비어있는 경우 할인가를 기준선으로 폴백해 쿠폰 할인분이 취소선/할인율로 노출되게 함
  const originalPrice = parsePrice(lowestProduct?.price) || rawSalePrice

  // 할인율 기준: 엔진오일·타이어·세차는 쿠폰적용가, 그 외는 판매가
  const discountBase = DISCOUNT_RATE_ON_COUPON_PRICE_SECTIONS.has(
    productGroup?.section ?? ''
  )
    ? salePrice
    : rawSalePrice

  const hasSalePrice = rawSalePrice > 0
  const discountRate =
    originalPrice > discountBase
      ? Math.round(((originalPrice - discountBase) / originalPrice) * 100)
      : 0
  const showOriginalPrice = originalPrice > 0 && discountRate > 0

  const displayName = category.name
  const displayDescription = category.description
  // 프론트 고정 이미지 우선, 없으면 API 상품 이미지로 폴백 (예: 맞춤 진단)
  const displayImageUrl =
    category.imageUrl || lowestProduct?.imageUrl || productGroup?.imageUrl

  return (
    <div className='flex w-full items-start gap-dimension-x2'>
      <div className='flex min-w-px flex-1 items-center gap-dimension-x1'>
        <div className='relative size-[56px] shrink-0 overflow-clip'>
          {displayImageUrl && (
            <Image
              src={displayImageUrl}
              alt={displayName}
              width='100%'
              height='100%'
              radius={0}
              className='absolute inset-0'
            />
          )}
        </div>
        <div className='flex h-16 min-w-px flex-1 flex-col items-start justify-center gap-0.5'>
          <Text
            variant='title-1-bold'
            color='text-foreground-neutral-strong'
            className='whitespace-nowrap'
          >
            {displayName}
          </Text>
          {displayDescription && (
            <Text
              variant='caption-1-medium'
              color='text-foreground-neutral-alternative'
              className='line-clamp-2 min-w-full flex-1 break-keep'
            >
              {displayDescription}
            </Text>
          )}
        </div>
      </div>
      <div className='flex shrink-0 flex-col items-end justify-center whitespace-nowrap text-right'>
        {COUPON_APPLICABLE_SECTIONS.has(category.section) && (
          <Text variant='title-2-semibold' color='text-foreground-brand-strong'>
            쿠폰적용가
          </Text>
        )}
        <Text variant='title-1-semibold' color='text-foreground-neutral-strong'>
          {hasSalePrice ? `${salePrice.toLocaleString()}원~` : '-'}
        </Text>
        {showOriginalPrice && (
          <div className='flex items-center gap-dimension-x1'>
            {discountRate > 0 && (
              <Text
                variant='title-2-semibold'
                color='text-foreground-brand-strong'
              >
                {discountRate}%
              </Text>
            )}
            <Text
              variant='body-1-medium'
              color='text-foreground-neutral-assistive'
              className='line-through'
            >
              {originalPrice.toLocaleString()}원
            </Text>
          </div>
        )}
      </div>
    </div>
  )
}

export default PlacePcStickyCta
